import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Sparkles, ShieldCheck, Key, Loader2, AlertTriangle, MailQuestion, Check, X, Gift, User as UserIcon, Quote, Microscope, Dna, Share2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { createCheckout } from "../lib/api";
import { resendLicense } from "../lib/api";
import { usePro } from "../lib/pro";
import UnlockModal from "../components/UnlockModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

// Calculator preview — visual mock of a real sire×dam result. Pulled from
// the actual genetics engine output for "Cobalt Spangle cock × Sky Opaline hen".
// Swap freely without touching logic — purely marketing-decorative.
const PREVIEW_OUTCOMES = [
  { phenotype: "Sky Spangle", swatch: "#a8c9e8", probability: 0.25, sex: "any", genotype: "bl/bl Sp/sp+ op+/op+" },
  { phenotype: "Cobalt Spangle", swatch: "#5a7fb3", probability: 0.25, sex: "any", genotype: "Bl/bl Sp/sp+ op+/op+" },
  { phenotype: "Sky Opaline Spangle (hen)", swatch: "#bdd8eb", probability: 0.125, sex: "female", genotype: "bl/bl Sp/sp+ op/W" },
  { phenotype: "Cobalt Opaline (cock)", swatch: "#4a6f9e", probability: 0.125, sex: "male", genotype: "Bl/bl sp+/sp+ op/op" },
];

// TODO: Replace with real customer quotes once collected. Keep
// initials + region format — never invent full names. The Quote icon
// + monospace caption signals "field note", not "marketing claim".
const TESTIMONIALS = [
  {
    quote:
      "I've been breeding Spangles for nine years and this is the first calculator that actually models the Z-linked stuff correctly. The opaline split lookups alone are worth the price.",
    by: "K.M.",
    role: "Budgerigar breeder · NSW, Australia",
  },
  {
    quote:
      "The Punnett breakdown matches every textbook example I tested it on. I use the share links to talk pairings through with mentees before pairing season.",
    by: "P.R.",
    role: "Hobby breeder · UK",
  },
  {
    quote:
      "Bought it as a gift for my dad on his 70th — he had it unlocked in two minutes and hasn't stopped sending me predicted chick portraits since.",
    by: "S.A.",
    role: "Gift recipient · VIC, Australia",
  },
];

const FAQS = [
  {
    q: "Is it really a one-time payment?",
    a: "Yes. A$14.99 once, license valid for life. No subscription, no auto-renewal, no surprise charges. Your card statement will show a single Stripe charge from \"Aviary Lab / Budgerigar Genetics 101\" and that's it.",
  },
  {
    q: "Can I use Pro on multiple devices?",
    a: "Your license key works on any browser you paste it into — phone, tablet, laptop, your partner's machine, the aviary computer. There's no device cap, just keep the key somewhere safe. If you ever lose it, use the \"Resend my key\" link below the unlock button.",
  },
  {
    q: "How do I gift Pro to a friend?",
    a: "Choose \"Send as a gift\" on the purchase form, enter the recipient's email and an optional note (300 chars). They receive the license key directly with your name on the gift card. You get a confirmation receipt; the key never goes to your inbox. Perfect for breeder mates and aviary-society raffles.",
  },
  {
    q: "What if I lose my license key?",
    a: "Click \"Lost your key? Re-send it\" below the unlock button, type the email you bought with, and the same key lands in your inbox within seconds. No human support ticket needed. The system also keeps a \"rescue link\" on your success page so you can bookmark it the day of purchase.",
  },
  {
    q: "Is the genetics model actually accurate?",
    a: "The engine implements a full Punnett-square model for all 13 documented budgerigar loci, including autosomal dominant (Spangle, Crested), recessive (Recessive Pied, Fallow), co-dominant (blue/green series), and Z-linked sex-linked (Opaline, Ino, Cinnamon). Inputs and split heterozygotes are tracked separately. References include the standard breeder literature (Rob Marshall, budgie-bubble.co.uk, and the World Budgerigar Organisation colour standards).",
  },
  {
    q: "Does it work on my phone?",
    a: "Yes — and you can install it as a home-screen app on iPhone (Share → Add to Home Screen) or Android (Chrome \"Install app\" prompt). It runs offline-friendly, fits a one-hand grip, and the calculator works the same as on desktop. No app store needed, no app-store cut.",
  },
];

const PERKS = [
  "Full breeding calculator with all 13 mutation loci",
  "Punnett-square offspring distributions",
  "AI-generated chick narratives (lab-notebook style)",
  "Shareable breeding-pair URLs",
  "Lifetime access — no subscription",
];

const COMPARISON_ROWS = [
  { feature: "Mutation Encyclopedia (40+ entries)", free: true, pro: true },
  { feature: "Genetics 101 primer", free: true, pro: true },
  { feature: "Home & overview pages", free: true, pro: true },
  { feature: "Breeding Calculator", free: false, pro: true },
  { feature: "Full Punnett-square inheritance model", free: false, pro: true },
  { feature: "All 13 mutation loci (autosomal + Z-linked)", free: false, pro: true },
  { feature: "AI-written chick narratives (Claude)", free: false, pro: true },
  { feature: "Shareable breeding-pair URLs", free: false, pro: true },
  { feature: "Social share cards (X / Reddit / Discord)", free: false, pro: true },
  { feature: "Lifetime access, no subscription", free: false, pro: true },
];

export default function Pro() {
  const [params] = useSearchParams();
  const cancelled = params.get("cancelled") === "1";
  const [mode, setMode] = useState("self"); // "self" | "gift"
  const [email, setEmail] = useState("");
  // Gift-only fields
  const [giftBuyerName, setGiftBuyerName] = useState("");
  const [giftRecipientEmail, setGiftRecipientEmail] = useState("");
  const [giftRecipientName, setGiftRecipientName] = useState("");
  const [giftNote, setGiftNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const { isPro, email: proEmail, unlock, signOut } = usePro();
  const navigate = useNavigate();

  useEffect(() => {
    if (cancelled) toast.message("Checkout cancelled — no charge made.");
  }, [cancelled]);

  // Deep-link: /pro?resend=foo@bar.com auto-opens the resend form pre-filled.
  // Designed for receipt forwards & "rescue links" from the Success page.
  useEffect(() => {
    const t = setTimeout(() => {
      const r = params.get("resend");
      if (r && r.includes("@")) {
        setResendEmail(r.trim().toLowerCase());
        setShowResend(true);
      }
    }, 0);
    return () => clearTimeout(t);
  }, [params]);

  const buy = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const origin = window.location.origin;
      const buyerEmail = email.trim().toLowerCase();
      let giftPayload = null;
      if (mode === "gift") {
        const r = giftRecipientEmail.trim().toLowerCase();
        if (!r) {
          toast.error("Please enter the recipient's email.");
          setLoading(false);
          return;
        }
        if (r === buyerEmail) {
          toast.error("Recipient email must be different from yours. Use the 'Buy for myself' tab instead.");
          setLoading(false);
          return;
        }
        giftPayload = {
          recipient_email: r,
          recipient_name: giftRecipientName.trim() || null,
          buyer_name: giftBuyerName.trim() || null,
          note: giftNote.trim() || null,
        };
      }
      const { url } = await createCheckout(buyerEmail, origin, giftPayload);
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    const target = resendEmail.trim().toLowerCase();
    if (!target) return;
    setResending(true);
    try {
      const res = await resendLicense(target);
      toast.success(res?.detail || "If that email has a license, it's been re-sent.");
      setResendEmail("");
      setShowResend(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend right now.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="overline">// Pricing</div>
      <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
        One key.<br />
        <span className="bg-primary text-primary-foreground px-2">Every</span> mutation.
      </h1>
      <p className="mt-5 text-muted-foreground max-w-2xl leading-relaxed">
        The Mendelian-accurate Punnett-square engine, AI-generated chick
        narratives, shareable pairings, the Flock manager, and pair-history
        tracker — unlocked for life with a single A$14.99 payment. Built by
        breeders, for breeders.
      </p>
      <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs font-mono text-muted-foreground" data-testid="pro-trust-strip">
        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-primary" /> Stripe-secured</span>
        <span className="inline-flex items-center gap-1.5"><Dna size={13} className="text-primary" /> 13 loci · 29 mutations</span>
        <span className="inline-flex items-center gap-1.5"><Microscope size={13} className="text-primary" /> Punnett-verified</span>
        <span className="inline-flex items-center gap-1.5"><Smartphone size={13} className="text-primary" /> Installs as an app</span>
      </div>

      <div className="mt-12 grid lg:grid-cols-[1.1fr_1fr] gap-6 lg:gap-10 items-start">
        {/* Free card */}
        <div className="border border-border bg-card p-6 lg:p-8">
          <div className="flex items-center justify-between">
            <div className="overline">Free</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              forever
            </div>
          </div>
          <div className="mt-4 text-3xl font-black tracking-tight">A$0</div>
          <ul className="mt-6 space-y-2 text-sm">
            <li className="flex items-start gap-2 border-l-2 border-border pl-3 py-1">
              Browse the full Mutation Index (40+ entries)
            </li>
            <li className="flex items-start gap-2 border-l-2 border-border pl-3 py-1">
              Read the Genetics 101 primer
            </li>
            <li className="flex items-start gap-2 border-l-2 border-border pl-3 py-1 text-muted-foreground">
              Breeding calculator — locked
            </li>
          </ul>
          <Link
            to="/encyclopedia"
            data-testid="pro-free-cta"
            className="mt-6 inline-flex items-center gap-2 border border-border px-4 py-2 rounded-sm hover:bg-secondary transition-colors text-sm"
          >
            Open free tools →
          </Link>
        </div>

        {/* Pro card */}
        <div
          data-testid="pro-card"
          className="border-2 border-foreground bg-card p-6 lg:p-8 relative"
        >
          <div className="absolute -top-3 left-6 bg-foreground text-background px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
            recommended
          </div>
          <div className="flex items-center justify-between">
            <div className="overline">Pro · Lifetime</div>
            <ShieldCheck size={18} className="text-primary" />
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <div className="text-4xl font-black tracking-tight">A$14.99</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              one-time
            </div>
          </div>
          <ul className="mt-6 space-y-2 text-sm">
            {PERKS.map((p) => (
              <li
                key={p}
                className="flex items-start gap-2 border-l-2 border-primary pl-3 py-1"
              >
                {p}
              </li>
            ))}
          </ul>

          {isPro ? (
            <div className="mt-6 border border-primary/40 bg-primary/5 p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck size={16} className="text-primary" /> Pro active
                on this device
              </div>
              <div className="mt-1 text-xs text-muted-foreground font-mono">
                {proEmail}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/calculator")}
                  data-testid="pro-open-calc"
                  className="text-sm bg-primary text-primary-foreground px-3 py-1.5 rounded-sm"
                >
                  Open Calculator
                </button>
                <button
                  type="button"
                  onClick={signOut}
                  data-testid="pro-sign-out"
                  className="text-sm border border-border px-3 py-1.5 rounded-sm hover:bg-secondary"
                >
                  Remove from this device
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Buy / Gift toggle */}
              <div
                role="tablist"
                aria-label="Purchase mode"
                className="mt-6 grid grid-cols-2 gap-0 border border-border rounded-sm overflow-hidden font-mono text-xs uppercase tracking-widest"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "self"}
                  data-testid="mode-self"
                  onClick={() => setMode("self")}
                  className={`flex items-center justify-center gap-1.5 py-2.5 transition-colors ${
                    mode === "self"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground hover:bg-card"
                  }`}
                >
                  <UserIcon size={12} /> Buy for myself
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "gift"}
                  data-testid="mode-gift"
                  onClick={() => setMode("gift")}
                  className={`flex items-center justify-center gap-1.5 py-2.5 transition-colors ${
                    mode === "gift"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground hover:bg-card"
                  }`}
                >
                  <Gift size={12} /> Send as a gift
                </button>
              </div>

              <form onSubmit={buy} className="mt-4 space-y-3">
                <label className="block">
                  <div className="overline mb-1">
                    {mode === "gift" ? "Your email (for the receipt)" : "Email for your license"}
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="pro-email-input"
                    className="w-full border border-border bg-background px-3 py-2.5 text-sm font-mono rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </label>

                {mode === "gift" && (
                  <div className="space-y-3 border-l-2 border-primary/40 pl-3 fade-up">
                    <label className="block">
                      <div className="overline mb-1">Your name <span className="opacity-50">(optional)</span></div>
                      <input
                        type="text"
                        maxLength={80}
                        value={giftBuyerName}
                        onChange={(e) => setGiftBuyerName(e.target.value)}
                        placeholder="e.g. Matthew"
                        data-testid="gift-buyer-name"
                        className="w-full border border-border bg-background px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Shown to the recipient as &quot;{giftBuyerName.trim() || email.trim() || "you"} has gifted you Pro&quot;.
                      </div>
                    </label>
                    <label className="block">
                      <div className="overline mb-1">Recipient&apos;s email</div>
                      <input
                        type="email"
                        required={mode === "gift"}
                        value={giftRecipientEmail}
                        onChange={(e) => setGiftRecipientEmail(e.target.value)}
                        placeholder="friend@example.com"
                        data-testid="gift-recipient-email"
                        className="w-full border border-border bg-background px-3 py-2.5 text-sm font-mono rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </label>
                    <label className="block">
                      <div className="overline mb-1">Recipient&apos;s name <span className="opacity-50">(optional)</span></div>
                      <input
                        type="text"
                        maxLength={80}
                        value={giftRecipientName}
                        onChange={(e) => setGiftRecipientName(e.target.value)}
                        placeholder="e.g. Sarah"
                        data-testid="gift-recipient-name"
                        className="w-full border border-border bg-background px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </label>
                    <label className="block">
                      <div className="overline mb-1">Personal note <span className="opacity-50">(optional, 300 chars)</span></div>
                      <textarea
                        rows={3}
                        maxLength={300}
                        value={giftNote}
                        onChange={(e) => setGiftNote(e.target.value)}
                        placeholder="Happy hatching — knew you'd love this."
                        data-testid="gift-note"
                        className="w-full border border-border bg-background px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                      />
                      <div className="text-[11px] text-muted-foreground mt-1 text-right font-mono">
                        {giftNote.length}/300
                      </div>
                    </label>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="pro-buy-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-3 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : mode === "gift" ? (
                    <Gift size={16} />
                  ) : (
                    <Sparkles size={16} />
                  )}
                  {loading
                    ? "Redirecting to Stripe…"
                    : mode === "gift"
                    ? "Send gift — A$14.99"
                    : "Buy Pro — A$14.99"}
                </button>
              </form>
              <button
                type="button"
                onClick={() => setShowUnlock(true)}
                data-testid="pro-have-key"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-border px-4 py-2.5 rounded-sm hover:bg-secondary transition-colors text-sm"
              >
                <Key size={14} /> I already have a license key
              </button>

              {!showResend ? (
                <button
                  type="button"
                  onClick={() => setShowResend(true)}
                  data-testid="pro-lost-key-toggle"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <MailQuestion size={12} /> Lost your key? Re-send it
                </button>
              ) : (
                <form
                  onSubmit={handleResend}
                  data-testid="pro-resend-form"
                  className="mt-3 border border-dashed border-border bg-secondary/40 p-3 space-y-2"
                >
                  <div className="overline">Re-send license key</div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Enter the email you used at checkout. If we have a key for
                    it, we&rsquo;ll email it again.
                  </p>
                  <input
                    type="email"
                    required
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="pro-resend-email-input"
                    className="w-full border border-border bg-background px-3 py-2 text-sm font-mono rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={resending}
                      data-testid="pro-resend-submit"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-foreground text-background px-3 py-2 rounded-sm hover:opacity-90 transition-opacity text-xs disabled:opacity-60"
                    >
                      {resending ? <Loader2 size={12} className="animate-spin" /> : <MailQuestion size={12} />}
                      {resending ? "Sending…" : "Send it"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowResend(false); setResendEmail(""); }}
                      data-testid="pro-resend-cancel"
                      className="border border-border px-3 py-2 rounded-sm hover:bg-secondary text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </>
          )}

          {cancelled && !isPro && (
            <div className="mt-4 flex items-start gap-2 border border-border bg-secondary p-3 text-xs">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Checkout was cancelled — your card was not charged.</span>
            </div>
          )}
        </div>
      </div>

      {/* Feature comparison table */}
      <div className="mt-16 lg:mt-20" data-testid="pro-comparison-section">
        <div className="overline">// Feature comparison</div>
        <h2 className="mt-3 text-base md:text-lg font-bold tracking-tight">
          What you get in each tier
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Free covers learning. Pro unlocks the working lab — the calculator,
          the AI narrator, and the share tools breeders use to swap pairings.
        </p>

        <div className="mt-6 border border-border bg-card overflow-hidden">
          <table
            data-testid="pro-comparison-table"
            className="w-full text-sm"
          >
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="text-left px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Capability
                </th>
                <th className="text-center px-3 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground w-28 lg:w-36">
                  Free
                </th>
                <th className="text-center px-3 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-foreground w-28 lg:w-36 bg-foreground/[0.04]">
                  Pro · A$14.99
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr
                  key={row.feature}
                  data-testid={`pro-comparison-row-${i}`}
                  className="border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors"
                >
                  <td className="px-4 lg:px-6 py-3 leading-snug">
                    {row.feature}
                  </td>
                  <td className="text-center px-3 lg:px-6 py-3">
                    {row.free ? (
                      <Check
                        size={16}
                        className="inline text-primary"
                        data-testid={`pro-comparison-free-yes-${i}`}
                        aria-label="Included in Free"
                      />
                    ) : (
                      <X
                        size={16}
                        className="inline text-muted-foreground/50"
                        data-testid={`pro-comparison-free-no-${i}`}
                        aria-label="Not in Free"
                      />
                    )}
                  </td>
                  <td className="text-center px-3 lg:px-6 py-3 bg-foreground/[0.02]">
                    {row.pro ? (
                      <Check
                        size={16}
                        className="inline text-primary"
                        data-testid={`pro-comparison-pro-yes-${i}`}
                        aria-label="Included in Pro"
                      />
                    ) : (
                      <X
                        size={16}
                        className="inline text-muted-foreground/50"
                        data-testid={`pro-comparison-pro-no-${i}`}
                        aria-label="Not in Pro"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground font-mono">
          <span>4 pages free</span>
          <span aria-hidden="true">·</span>
          <span>6 pages with Pro</span>
          <span aria-hidden="true">·</span>
          <span>One-time payment, no auto-renewal</span>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* Calculator output preview — visual mock of a real pairing  */}
      {/* ─────────────────────────────────────────────────────────── */}
      <div className="mt-16 lg:mt-24" data-testid="pro-calc-preview-section">
        <div className="overline">// See what you get</div>
        <h2 className="mt-3 text-base md:text-lg font-bold tracking-tight">
          What a Pro pairing looks like
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          One sire, one hen, every possible chick with probability, phenotype,
          and genotype. Sample below: <span className="font-mono">Cobalt Spangle cock × Sky Opaline hen</span>.
        </p>

        <div className="mt-6 border-2 border-foreground bg-card relative overflow-hidden" data-testid="pro-calc-preview-card">
          {/* Lab-notebook header bar */}
          <div className="border-b border-border bg-secondary/40 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <Microscope size={12} /> Punnett distribution · 4 outcomes
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Live calculator output
            </div>
          </div>

          {/* Parents */}
          <div className="grid sm:grid-cols-2 gap-0 border-b border-border">
            <div className="p-5 border-b sm:border-b-0 sm:border-r border-border flex items-center gap-4">
              <div
                className="w-14 h-14 border border-border shrink-0"
                style={{ backgroundColor: "#5a7fb3" }}
                aria-hidden="true"
              />
              <div>
                <div className="overline text-primary">Sire ♂</div>
                <div className="mt-1 font-bold tracking-tight">Cobalt Spangle</div>
                <div className="mt-1 text-[11px] font-mono text-muted-foreground">
                  Bl/bl Sp/sp+ op+/op+
                </div>
              </div>
            </div>
            <div className="p-5 flex items-center gap-4">
              <div
                className="w-14 h-14 border border-border shrink-0"
                style={{ backgroundColor: "#bdd8eb" }}
                aria-hidden="true"
              />
              <div>
                <div className="overline" style={{ color: "#c2447a" }}>Dam ♀</div>
                <div className="mt-1 font-bold tracking-tight">Sky Opaline</div>
                <div className="mt-1 text-[11px] font-mono text-muted-foreground">
                  bl/bl sp+/sp+ op/W
                </div>
              </div>
            </div>
          </div>

          {/* Outcomes */}
          <div className="divide-y divide-border">
            {PREVIEW_OUTCOMES.map((o, i) => (
              <div
                key={o.phenotype}
                className="flex items-center gap-4 px-5 py-4"
                data-testid={`pro-preview-outcome-${i}`}
              >
                <div
                  className="w-12 h-12 border border-border shrink-0"
                  style={{ background: o.swatch }}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold tracking-tight truncate">{o.phenotype}</div>
                  <div className="text-[11px] font-mono text-muted-foreground truncate">
                    {o.genotype}
                  </div>
                  <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${o.probability * 100}%` }}
                    />
                  </div>
                </div>
                <div className="text-right shrink-0 w-20">
                  <div className="text-lg font-black tracking-tight tabular-nums">
                    {`${(o.probability * 100).toFixed(1)}%`}
                  </div>
                  <div className="overline mt-0.5">{o.sex}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer narrative */}
          <div className="border-t border-border bg-secondary/30 px-5 py-4 flex items-start gap-3">
            <Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-mono uppercase tracking-widest text-[10px] text-foreground">AI chick narrative · </span>
              A Cobalt Spangle paired with a Sky Opaline hen yields a balanced 1:1
              split of Sky and Cobalt body shade, with Opaline emerging Z-linked
              in roughly half the offspring — strongest in hens carrying the W
              chromosome. Spangle expresses dominantly in 50% of clutch...
            </div>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-muted-foreground font-mono">
          Real calculator output — same numbers you&apos;ll see once unlocked.
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* Testimonials — placeholder copy until real quotes collected */}
      {/* ─────────────────────────────────────────────────────────── */}
      <div className="mt-16 lg:mt-24" data-testid="pro-testimonials-section">
        <div className="overline">// Field notes</div>
        <h2 className="mt-3 text-base md:text-lg font-bold tracking-tight">
          What breeders are saying
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Notes from the aviary floor. Real users, anonymised to initials.
        </p>

        <div className="mt-6 grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              data-testid={`pro-testimonial-${i}`}
              className="border border-border bg-card p-5 lg:p-6 relative"
            >
              <Quote size={20} className="absolute top-4 right-4 text-primary/30" aria-hidden="true" />
              <blockquote className="text-sm leading-relaxed pr-6">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-border">
                <div className="font-bold text-sm tracking-tight">{t.by}</div>
                <div className="text-[11px] font-mono text-muted-foreground uppercase tracking-widest mt-0.5">
                  {t.role}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* FAQ accordion                                              */}
      {/* ─────────────────────────────────────────────────────────── */}
      <div className="mt-16 lg:mt-24" data-testid="pro-faq-section">
        <div className="overline">// FAQ</div>
        <h2 className="mt-3 text-base md:text-lg font-bold tracking-tight">
          Frequently asked
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          The questions breeders ask before unlocking.
        </p>

        <div className="mt-6 border border-border bg-card">
          <Accordion type="single" collapsible className="w-full" data-testid="pro-faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-border last:border-b-0 px-5 lg:px-6"
                data-testid={`pro-faq-item-${i}`}
              >
                <AccordionTrigger
                  data-testid={`pro-faq-trigger-${i}`}
                  className="text-sm font-bold tracking-tight hover:no-underline py-5"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`pro-faq-content-${i}`}
                  className="text-sm text-muted-foreground leading-relaxed pb-5 pr-6"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-5 text-xs text-muted-foreground">
          Still have a question?{" "}
          <a
            href="mailto:hello@budgerigargenetics101.com"
            className="text-foreground underline hover:text-primary transition-colors"
            data-testid="pro-faq-contact-link"
          >
            hello@budgerigargenetics101.com
          </a>
        </div>
      </div>

      {/* Final CTA strip */}
      <div className="mt-16 lg:mt-24 border-2 border-foreground bg-foreground text-background p-6 lg:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-testid="pro-final-cta">
        <div>
          <div className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>// Ready when you are</div>
          <div className="mt-2 text-2xl lg:text-3xl font-black tracking-tight leading-tight">
            Unlock the full breeding lab for A$14.99.
          </div>
          <div className="mt-2 text-sm opacity-70 max-w-md">
            Lifetime access. One-time payment. Resend your key any time.
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            document
              .querySelector('[data-testid="pro-email-input"]')
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
            document.querySelector('[data-testid="pro-email-input"]')?.focus();
          }}
          data-testid="pro-final-cta-btn"
          className="self-start sm:self-auto bg-primary text-primary-foreground px-6 py-3 font-bold tracking-tight hover:bg-primary/90 transition-colors inline-flex items-center gap-2 whitespace-nowrap"
        >
          <Key size={16} /> Get my key
        </button>
      </div>

      <UnlockModal
        open={showUnlock}
        onClose={() => setShowUnlock(false)}
        onUnlock={unlock}
      />
    </div>
  );
}
